/**
 * WebSocket Service — M1 Real-Time Transport
 * ============================================
 *
 * Singleton service managing a single WebSocket connection to /api/ws.
 *
 * Features:
 *  - connect / disconnect with JWT auth
 *  - join / leave thread rooms
 *  - sendMessage (persist + broadcast via backend)
 *  - sendTyping (broadcast-only)
 *  - Event subscription: on(event, handler) / off(event, handler)
 *  - Auto-reconnect with exponential backoff (2s → 30s cap)
 *  - Automatic room re-join on reconnect
 *  - QUOTA_EXCEEDED error surfaced via 'quota_exceeded' event
 *
 * Protocol (mirrors backend websocket_routes.py):
 *  Client → Server:
 *    { action: 'join',   thread_id }
 *    { action: 'leave',  thread_id }
 *    { action: 'send',   thread_id, content, message_type }
 *    { action: 'typing', thread_id }
 *
 *  Server → Client:
 *    { event: 'connected',   data }
 *    { event: 'joined',      data }
 *    { event: 'left',        data }
 *    { event: 'new_message', data }
 *    { event: 'typing',      data }
 *    { event: 'error',       data: { code, detail } }
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const RECONNECT_BASE = 2000;
const RECONNECT_CAP = 30000;

function buildWsUrl(token) {
  // TREATX-L1F-DNS-ACTIVATION (production correction) — same-origin WS
  // when no explicit base is configured. Derives ws:/wss: from the
  // page's own protocol/host so the request preserves the trusted
  // X-Forwarded-Host the L1-F resolver keys off.
  let base;
  if (API_BASE) {
    base = API_BASE.replace(/^http/, 'ws');
  } else if (typeof window !== 'undefined' && window.location) {
    const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
    base = `${scheme}://${window.location.host}`;
  } else {
    base = '';
  }
  return `${base}/api/ws?token=${encodeURIComponent(token)}`;
}

class WebSocketService {
  constructor() {
    /** @type {WebSocket|null} */
    this._ws = null;
    /** @type {Map<string, Set<Function>>} */
    this._listeners = new Map();
    this._token = null;
    this._attempt = 0;
    this._timer = null;
    /** Threads the user has joined — re-joined on reconnect */
    this._joinedThreads = new Set();
    /** 'disconnected' | 'connecting' | 'connected' | 'reconnecting' */
    this._state = 'disconnected';
    this._stateListeners = new Set();
  }

  // ── Public API ──────────────────────────────────────────────────

  get state() { return this._state; }
  get isConnected() { return this._state === 'connected'; }

  /**
   * Open a WebSocket connection.
   * @param {string} token  JWT token for auth
   */
  connect(token) {
    if (!token) return;
    this._token = token;
    this._teardown();
    this._open();
  }

  /** Gracefully close and stop reconnecting. */
  disconnect() {
    this._token = null;
    this._joinedThreads.clear();
    this._teardown();
    this._setState('disconnected');
  }

  /** Join a thread room (idempotent). */
  join(threadId) {
    if (!threadId) return;
    this._joinedThreads.add(threadId);
    this._send({ action: 'join', thread_id: threadId });
  }

  /** Leave a thread room. */
  leave(threadId) {
    if (!threadId) return;
    this._joinedThreads.delete(threadId);
    this._send({ action: 'leave', thread_id: threadId });
  }

  /**
   * Send a message via WebSocket (backend persists + broadcasts).
   * @param {string} threadId
   * @param {string} content
   * @param {string} messageType  'text' | 'file' | 'image'
   */
  sendMessage(threadId, content, messageType = 'text') {
    this._send({
      action: 'send',
      thread_id: threadId,
      content,
      message_type: messageType,
    });
  }

  /** Broadcast a typing indicator (no persistence). */
  sendTyping(threadId) {
    this._send({ action: 'typing', thread_id: threadId });
  }

  /** Subscribe to a server event. Returns unsubscribe function. */
  on(event, handler) {
    if (!this._listeners.has(event)) {
      this._listeners.set(event, new Set());
    }
    this._listeners.get(event).add(handler);
    return () => this.off(event, handler);
  }

  /** Unsubscribe from a server event. */
  off(event, handler) {
    this._listeners.get(event)?.delete(handler);
  }

  /** Subscribe to connection state changes. */
  onStateChange(handler) {
    this._stateListeners.add(handler);
    return () => this._stateListeners.delete(handler);
  }

  // ── Internal ────────────────────────────────────────────────────

  _setState(s) {
    if (this._state === s) return;
    this._state = s;
    this._stateListeners.forEach((fn) => { try { fn(s); } catch {} });
  }

  _open() {
    this._setState('connecting');
    const url = buildWsUrl(this._token);

    try {
      this._ws = new WebSocket(url);
    } catch {
      this._scheduleReconnect();
      return;
    }

    this._ws.onopen = () => {
      // Wait for 'connected' event before marking as connected
    };

    this._ws.onmessage = (evt) => {
      this._handleMessage(evt.data);
    };

    this._ws.onclose = (evt) => {
      this._ws = null;
      // 4001 = auth failure — don't reconnect
      if (evt.code === 4001) {
        this._setState('disconnected');
        this._emit('auth_failed', { reason: evt.reason });
        return;
      }
      if (this._token) {
        this._scheduleReconnect();
      } else {
        this._setState('disconnected');
      }
    };

    this._ws.onerror = () => {
      // onclose will fire after onerror
    };
  }

  _handleMessage(raw) {
    let msg;
    try { msg = JSON.parse(raw); } catch { return; }

    const { event, data } = msg;

    if (event === 'connected') {
      this._setState('connected');
      this._attempt = 0;
      // Re-join previously joined threads after reconnect
      this._joinedThreads.forEach((tid) => {
        this._send({ action: 'join', thread_id: tid });
      });
    }

    // Surface QUOTA_EXCEEDED as a dedicated event
    if (event === 'error' && data?.code === 'QUOTA_EXCEEDED') {
      this._emit('quota_exceeded', data);
    }

    this._emit(event, data);
  }

  _emit(event, data) {
    const handlers = this._listeners.get(event);
    if (!handlers) return;
    handlers.forEach((fn) => { try { fn(data); } catch (e) { console.error('[WS] handler error:', e); } });
  }

  _send(payload) {
    if (this._ws?.readyState === WebSocket.OPEN) {
      this._ws.send(JSON.stringify(payload));
    }
  }

  _scheduleReconnect() {
    this._setState('reconnecting');
    const delay = Math.min(RECONNECT_BASE * Math.pow(2, this._attempt), RECONNECT_CAP);
    this._attempt += 1;
    this._timer = setTimeout(() => {
      this._timer = null;
      if (this._token) this._open();
    }, delay);
  }

  _teardown() {
    if (this._timer) {
      clearTimeout(this._timer);
      this._timer = null;
    }
    if (this._ws) {
      // Prevent reconnect from onclose
      this._ws.onclose = null;
      this._ws.onerror = null;
      this._ws.onmessage = null;
      this._ws.close();
      this._ws = null;
    }
  }
}

/** Singleton instance */
export const wsService = new WebSocketService();
export default wsService;
